import { Button, Tooltip, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import isHotkey from 'is-hotkey';
import { useEffect, useState } from 'react';
import { EncounterTemplateInputNode } from './EncounterTemplateInputNode';
import { EncounterTemplateTreeNode } from './useEncounterTemplateTreeDataSource';
import { EncounterTemplateTreeDataNode } from '@app/medical-record/widgets/EncounterTemplates.List';
import { Icon } from '@app/ui';
import { EncounterTemplate } from '@app/medical-record/api';

const isAltEnter = isHotkey('alt+enter');

interface Props {
  node: EncounterTemplateTreeDataNode;
  folderOnly?: boolean;
  onAddFolder: (value: EncounterTemplateTreeNode) => void;
  onAddTemplate: (value: EncounterTemplate) => void;
  showAddTemplate?: boolean;
}

export type EncounterTemplateInputType = 'FOLDER' | 'TEMPLATE' | undefined;

function useEditState() {
  const [edit, setEdit] = useState<EncounterTemplateInputType>();

  useEffect(() => {
    const callback = (e: KeyboardEvent) => isAltEnter(e) && setEdit('TEMPLATE');
    document.addEventListener('keydown', callback);
    return () => document.removeEventListener('keydown', callback);
  }, [setEdit]);

  const cancelEdit = () => setEdit(undefined);

  return [edit, setEdit, cancelEdit] as const;
}

export function AddEncounterTemplateTreeNode(props: Props) {
  const { node, folderOnly, onAddFolder, onAddTemplate, showAddTemplate } = props;
  const { t } = useTranslation(undefined, { keyPrefix: 'encounterTemplates.nav' });
  const [edit, setEdit, cancelEdit] = useEditState();

  const handleAddTemplate = (value: string) => {
    const nodeToAdd = node.folder as EncounterTemplateTreeNode;

    onAddTemplate({
      id: 'NEW',
      directory: nodeToAdd.directory ?? null,
      root: nodeToAdd.root,
      note: null,
      name: value,
    });
    cancelEdit();
  };

  const addFolder = (value: string) => {
    onAddFolder({
      children: [],
      templates: [],
      name: value,
      fullPath: `${node.folder?.fullPath || ''}${value}/`,
      directory: node.folder ? (node.folder.directory || '') + value + '/' : undefined,
      root: node.folder?.root || value,
      loaded: true,
    });
    cancelEdit();
  };

  switch (edit) {
    case 'FOLDER':
      return <EncounterTemplateInputNode type={edit} onCancel={cancelEdit} onSubmit={addFolder} />;
    case 'TEMPLATE':
      return <EncounterTemplateInputNode type={edit} onCancel={cancelEdit} onSubmit={handleAddTemplate} />;
  }

  return (
    <Space direction="horizontal">
      {!folderOnly && showAddTemplate && (
        <Tooltip title={t('templates.tooltip')}>
          <Button
            type="link"
            iconPosition="end"
            icon={<Icon type="add-file" />}
            onClick={() => setEdit('TEMPLATE')}
            className="italic text-gray-400 block text-base hover:bg-gray-200 rounded-md p-1"
            aria-label={t('templates.tooltip')}
          />
        </Tooltip>
      )}
      <Tooltip title={t('folders.tooltip')}>
        <Button
          type="link"
          iconPosition="end"
          icon={<Icon type="add-folder" />}
          onClick={() => setEdit('FOLDER')}
          className="italic text-gray-400 block text-base hover:bg-gray-200 rounded-md p-1"
        >
          {folderOnly ? <span className="ml-1">{t('folders.add')}</span> : null}
        </Button>
      </Tooltip>
    </Space>
  );
}
